import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  API,
  CullDTO,
  GeoCullDTO,
  InventoryAggregationDTO,
  InventoryFieldDefinitionDTO,
  InventoryFieldDTO,
  InventoryFieldValueAggregationDTO,
  InventoryFilterDTO,
  InventoryFilterValueListResponseDTO,
  Pagination,
  PlayerCullDTO,
  ScreenScope,
} from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { InventoryDetailsComponent } from '../inventory-details/inventory-details.component';
import { BehaviorSubject } from 'rxjs';
import { ErrorService } from '../../../services/error.service';
import { InventoryCartService } from '../../../services/inventory/inventory-cart.service';
import { MsOptions } from '../../../services/global/global-interfaces';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { StringTransformationService } from 'src/app/services/transformation-helper/string-transformation.service';

export interface InventoryFieldValueAggregationDTOFrontendEnhanced extends InventoryFieldValueAggregationDTO {
  report?: InventoryAggregationDTO;
  collapsed?: boolean;
}

@Component({
  selector: 'sb-comp-inventory-list',
  templateUrl: './inventory-list.component.html',
  styleUrls: ['./inventory-list.component.scss'],
  standalone: false,
})
export class InventoryListComponent implements OnInit, ModalNavigationComponent {
  readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  @Input() isInIntegratedContext: boolean;
  listItems: InventoryFieldValueAggregationDTOFrontendEnhanced[] = [];

  currentSelection: InventoryFieldDTO;
  inventoryFields: InventoryFieldDTO[] = [];
  @Input() cartFilter: any;
  @Output() playerCullChange: EventEmitter<PlayerCullDTO | null> = new EventEmitter<PlayerCullDTO | null>();
  @Output() geoCullChange: EventEmitter<GeoCullDTO | null> = new EventEmitter<GeoCullDTO | null>();
  @Output() refreshData: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() cartAdd: EventEmitter<any> = new EventEmitter<any>();
  resultCount: number;
  loading: boolean;
  maxSize: number;
  paginationString: string;
  boundaryLinks: boolean;
  collectionSize: number;
  pagination: BehaviorSubject<Pagination>;
  paginationInitialized: boolean;
  sizes: number[];
  pos: { x: any; y: any };
  isInModalContext: boolean;
  search: string;
  debounceTimer: ReturnType<typeof setTimeout>;
  playerCulls: CullDTO[];
  geoCulls: CullDTO[];
  filter: InventoryFilterDTO[];
  msPlayerCullOptions: MsOptions;
  msGeoCullOptions: MsOptions;

  screenScopes: string[];
  screenScope: string;

  constructor(
    public readonly translate: TranslateService,
    public readonly util: UtilService,
    private readonly errorHandler: ErrorService,
    private readonly modalService: ModalService,
    private readonly api: API,
    private readonly inventoryService: InventoryCartService,
    private readonly modalNavigation: ModalNavigationService,
    public readonly permissionService: PermissionService
  ) {
    this.screenScopes = Object.keys(ScreenScope);
    this.screenScope = 'ALL';
    this.debounceTimer = null;
    this.playerCulls = [];
    this.geoCulls = [];
    this.resultCount = -1;
    this.maxSize = 8;
    this._page = 1;
    this.paginationString = '';
    this.boundaryLinks = true;
    this.collectionSize = -1;
    this.paginationInitialized = false;
    this.sizes = [8, 12, 16, 20, 24, 50, 100];
    this.search = '';
    this.filter = [];
    this.pagination = new BehaviorSubject<Pagination>({
      page: this.page,
      count: this.maxSize ? this.maxSize : 8,
      pages: -1,
      resultcount: -1,
    });
  }

  _playerCull: PlayerCullDTO;

  get playerCull(): PlayerCullDTO {
    return this._playerCull;
  }

  @Input() set playerCull(cull: PlayerCullDTO) {
    this._playerCull = cull;
    this.inventoryService.playerCull = cull;
  }

  _geoCull: GeoCullDTO;

  get geoCull(): GeoCullDTO {
    return this._geoCull;
  }

  @Input() set geoCull(cull: GeoCullDTO) {
    this._geoCull = cull;
    this.inventoryService.geoCull = cull;
  }

  // tslint:disable-next-line:variable-name
  _page: number;

  get page(): number {
    return this._page;
  }

  set page(val: number) {
    this._page = val;
    this.pagination.value.page = val;
  }

  pageChange($event: number) {
    this.page = $event;
    this.requestDebounced();
  }

  setMaxSize(size: number) {
    this.maxSize = size;
    this.pagination.value.count = size;
    this.requestDebounced();
  }

  exportInventory() {
    setTimeout(() => {
      this.loading = true;
      this.api
        .ds_rm_InventoryScreen_export({
          playerCull: this.playerCull ? [this.playerCull.id] : null,
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: data => {
            this.util.downloadFileLink(data);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  ngOnInit(): void {
    if (this.isInModalContext) {
      this.playerCull = this.modalNavigation.getCurrentNavigation()?.data?.playerCull;
      this.geoCull = this.modalNavigation.getCurrentNavigation()?.data?.geoCull;
    }
    this.api
      .ds_rm_Inventory_fields()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (inventoryFields: InventoryFieldDefinitionDTO[]) => {
          for (const d of inventoryFields) {
            if (!d.field && d.type !== 'LocationTypeDTO') {
              // second argument removes Touchpoint option
              this.inventoryFields.push(d);
            }
          }
          if (this.isInModalContext) {
            const selection: number = this.modalNavigation.getCurrentNavigation()?.data?.selection;
            const selectionArray: string[] = [
              'InventoryClientDTO',
              'InventoryChannelDTO',
              'InventoryMarkerDTO',
              'InventoryLocationDTO',
              'InventoryDeviceDTO',
              'InventoryScreenDTO',
            ];
            this.currentSelection = this.inventoryFields.filter(x => x.type === selectionArray[selection || 0])[0];
          } else {
            this.currentSelection = this.inventoryFields[0];
          }
          this.initPagination();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  initPagination() {
    this.pagination.subscribe((e: Pagination) => {
      this.collectionSize = e.resultcount;
      this.maxSize = e.count;
      this.resultCount = e.resultcount;
    });
    this.requestDebounced();
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.api
      .ds_rm_Inventory_filterValues(
        {
          selection: this.currentSelection,
          filters: [],
          whitelistPolygons: null,
          blacklistPolygons: null,
          playerCulls: this.playerCull ? [this.playerCull] : null,
          geoCulls: this.geoCull ? [this.geoCull] : null,
          descriptorId: null,
        },
        {
          search: this.stringTransformation.encodeSearchQueryParam(false, this.search),
          screenScope: ScreenScope[this.screenScope],
        },
        this.pagination
      )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: InventoryFilterValueListResponseDTO) => {
          this.listItems = response.values;
          this.listItems.forEach(x => {
            x.collapsed = true;
          });
          this.paginationString = this.stringTransformation.getPaginationString(
            this.page,
            this.maxSize,
            this.listItems.length,
            this.resultCount
          );
          this.paginationInitialized = true;
          this.inventoryService.collectElementsForAggregation(this.currentSelection.type, this.listItems).then(list => {
            this.listItems = list;
          });
        },
      });
  }

  addToFilter(ele: InventoryFieldValueAggregationDTOFrontendEnhanced) {
    if (ele.collapsed === false) {
      ele.collapsed = true;
    } else {
      for (const e of this.listItems) {
        e.collapsed = true;
      }
      ele.collapsed = false;
    }
    this.filter = [];
    this.filter.push(this.inventoryService.buildFilterElement(ele, this.currentSelection.type));
  }

  addToCart(ele: InventoryFieldValueAggregationDTOFrontendEnhanced) {
    this.cartAdd.emit({ element: ele, type: this.currentSelection.type });
  }

  doSearch($event: string) {
    this.search = $event;
    this.requestDebounced();
  }

  getSelectedLocationCoords(company: any) {
    if (company?.address?.position?.coordinates) {
      this.pos = { x: company.address.position.coordinates[0], y: company.address.position.coordinates[1] };
    }
  }

  scopeSelection(event: InventoryFieldDTO) {
    this.currentSelection = event;
    this.page = 1;
    this.requestDebounced();
  }

  showInventoryDetails(inventoryLocation: InventoryFieldValueAggregationDTOFrontendEnhanced) {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: InventoryDetailsComponent, data: { location: inventoryLocation } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then(() => {})
      .catch(() => {});
  }

  isInCart(ele: InventoryFieldValueAggregationDTOFrontendEnhanced): boolean {
    if (this.cartFilter && this.cartFilter.length > 0) {
      for (const cartItem of this.cartFilter) {
        for (const cartVal of cartItem.values) {
          if (ele.displayValue.id === cartVal.displayValue.id) {
            return true;
          }
        }
      }
    }
  }

  update($event: any, type: string) {
    switch (type) {
      case 'PLAYER_CULLS':
        if ($event && $event?.length > 0) {
          this.playerCull = $event[0];
        } else {
          this.playerCull = null;
        }
        this.playerCullChange.emit(this.playerCull);
        break;
      case 'GEO_CULLS':
        if ($event && $event?.length > 0) {
          this.geoCull = $event[0];
        } else {
          this.geoCull = null;
        }
        this.geoCullChange.emit(this.geoCull);
        break;
    }
    this.refreshFromServer();
    this.refreshData.emit(true);
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'PLAYER_CULLS':
        if (options) {
          this.msPlayerCullOptions = options;
        }
        if (this.msPlayerCullOptions?.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msPlayerCullOptions?.search),
                type: 'PlayerCull',
              },
              this.msPlayerCullOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (culls: CullDTO[]) => {
                this.playerCulls = culls;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
      case 'GEO_CULLS':
        if (options) {
          this.msGeoCullOptions = options;
        }
        if (this.msGeoCullOptions?.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msGeoCullOptions.search),
                type: 'GeoCull',
              },
              this.msGeoCullOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (culls: CullDTO[]) => {
                this.geoCulls = culls;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
    }
  }
}
